import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { ref, set, onValue, remove, update } from 'firebase/database';
import { onValueThrottled } from '../utils/rtdbThrottle';
import {
  FiMonitor, FiPlay, FiStopCircle, FiVideo, FiVolume2, FiVolumeX,
  FiHome, FiArrowLeft, FiArrowRight, FiArrowUp, FiArrowDown,
  FiPower, FiRotateCcw, FiType, FiSquare, FiSend, FiMousePointer,
  FiRefreshCw, FiMinus, FiPlus, FiCornerDownLeft
} from 'react-icons/fi';

export default function RemotePage() {
  const [searchParams] = useSearchParams();
  const [deviceId, setDeviceId] = useState('');
  const [devices, setDevices] = useState({});
  const [sessionInfo, setSessionInfo] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState('disconnected');
  const [fps, setFps] = useState(0);
  const [textInput, setTextInput] = useState('');
  const [clickMode, setClickMode] = useState(true);
  const [currentFrameUrl, setCurrentFrameUrl] = useState(null);
  const screenRef = useRef(null);
  const frameCountRef = useRef(0);
  const lastFpsUpdateRef = useRef(Date.now());
  const lastFrameTimeRef = useRef(0);

  // Set device from URL query parameter
  useEffect(() => {
    const deviceFromUrl = searchParams.get('device');
    if (deviceFromUrl) {
      setDeviceId(deviceFromUrl);
    }
  }, [searchParams]);

  // Monitor devices
  useEffect(() => {
    const devicesRef = ref(db, 'mdm/devices');
    const unsubscribe = onValueThrottled(devicesRef, (snapshot) => {
      const devicesData = snapshot.val() || {};
      setDevices(devicesData);
    });
    return () => unsubscribe();
  }, []);

  // Listen for device streaming state changes (Wasabi relay mode)
  // Frames are uploaded to Wasabi, URLs are pushed to Firebase
  useEffect(() => {
    if (!deviceId || !sessionInfo) return;

    const streamingRef = ref(db, `mdm/devices/${deviceId}/streaming`);
    const unsubscribe = onValue(streamingRef, (snapshot) => {
      const streamingData = snapshot.val();
      if (streamingData) {
        // Check streaming status
        if (streamingData.status === 'streaming' || streamingData.status === 'ready') {
          setStreamStatus('connected');
          setIsStreaming(true);
        } else if (streamingData.status === 'stopped') {
          setStreamStatus('disconnected');
          setIsStreaming(false);
          setCurrentFrameUrl(null);
        } else if (streamingData.status === 'error') {
          setStreamStatus('error');
        }

        // New frame available from Wasabi
        if (streamingData.frameUrl && streamingData.frameTime) {
          // Only update if this is a new frame
          if (streamingData.frameTime > lastFrameTimeRef.current) {
            lastFrameTimeRef.current = streamingData.frameTime;
            // Add cache-busting timestamp to force browser to fetch new image
            const cacheBustedUrl = `${streamingData.frameUrl}?t=${streamingData.frameTime}`;
            setCurrentFrameUrl(cacheBustedUrl);

            // Calculate FPS
            frameCountRef.current++;
            const now = Date.now();
            if (now - lastFpsUpdateRef.current >= 1000) {
              setFps(frameCountRef.current);
              frameCountRef.current = 0;
              lastFpsUpdateRef.current = now;
            }
          }
        }
      } else {
        // Streaming data cleared - session ended
        if (isStreaming) {
          setStreamStatus('disconnected');
          setIsStreaming(false);
          setCurrentFrameUrl(null);
          setFps(0);
        }
      }
    });
    return () => unsubscribe();
  }, [deviceId, sessionInfo, isStreaming]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop streaming session if active when unmounting
      if (deviceId && sessionInfo) {
        update(ref(db, `mdm/devices/${deviceId}`), {
          command: 'stop_stream',
          streamSessionId: null,
        }).catch(console.error);
      }
    };
  }, [deviceId, sessionInfo]);

  // Start remote session
  const startRemoteSession = async () => {
    if (!deviceId) return alert('Select a device');

    const device = devices[deviceId];
    if (!device) return alert('Device not found');

    // Check if device is online
    const lastSeen = device.last_time_seen;
    const now = Date.now();
    if (!lastSeen || now - lastSeen > 60000) {
      return alert('Device appears to be offline. Last seen: ' +
        (lastSeen ? new Date(lastSeen).toLocaleString() : 'Never'));
    }

    const sessionId = `session_${Date.now()}`;

    try {
      // Send command to device to start streaming
      await update(ref(db, `mdm/devices/${deviceId}`), {
        command: 'start_stream',
        streamSessionId: sessionId,
      });

      setSessionInfo({ sessionId, startTime: Date.now() });
      setStreamStatus('waiting');
    } catch (error) {
      alert(`Failed to start session: ${error.message}`);
    }
  };

  // Stop remote session
  const stopRemoteSession = async () => {
    if (!deviceId) return;

    try {
      await update(ref(db, `mdm/devices/${deviceId}`), {
        command: 'stop_stream',
        streamSessionId: null,
      });

      // Clear streaming state
      await remove(ref(db, `mdm/devices/${deviceId}/streaming`));
    } catch (error) {
      console.error('Failed to stop session:', error);
    }

    // Reset state
    setSessionInfo(null);
    setIsStreaming(false);
    setStreamStatus('disconnected');
    setCurrentFrameUrl(null);
    setFps(0);
    lastFrameTimeRef.current = 0;
  };

  // Send remote command via Firebase
  const sendRemoteCommand = async (command) => {
    if (!deviceId) return alert('Select a device');
    try {
      const cmdRef = ref(db, `mdm/devices/${deviceId}/command`);
      await set(cmdRef, command);
      // The agent clears the command itself once it has run it (resetCommand() on both the success
      // and the failure path). We must NOT clear it here: Firebase delivers the current value of a
      // field, not a queue, so wiping it a second later means a device that is even slightly slow
      // only ever sees the empty string and the tap never happens. Measured on a Hisense DM66D,
      // where the same tap sent by hand and left in place was injected without trouble.
    } catch (error) {
      alert(`Failed to send command: ${error.message}`);
    }
  };

  // Send text input
  const sendTextInput = async () => {
    if (!textInput.trim()) return;
    await sendRemoteCommand(`input_text:${textInput}`);
    setTextInput('');
  };

  // Handle click on screen
  const handleScreenClick = async (e) => {
    if (!clickMode || !isStreaming) return;

    const rect = screenRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    // Send tap command with normalized coordinates (0-1)
    await sendRemoteCommand(`tap:${x.toFixed(4)}:${y.toFixed(4)}`);
  };

  // Refresh single screenshot
  const refreshScreenshot = async () => {
    await sendRemoteCommand('screenshot_stream');
  };

  // Get device status indicator
  const getDeviceStatus = (device) => {
    if (!device?.last_time_seen) return { color: 'bg-gray-500', text: 'Unknown' };
    const now = Date.now();
    const diff = now - device.last_time_seen;
    if (diff < 60000) return { color: 'bg-green-500', text: 'Online' };
    if (diff < 300000) return { color: 'bg-yellow-500', text: 'Idle' };
    return { color: 'bg-red-500', text: 'Offline' };
  };

  const selectedDevice = devices[deviceId];
  const deviceStatus = selectedDevice ? getDeviceStatus(selectedDevice) : null;

  return (
    <div className="page-shell">
      <div className="flex items-center gap-3 mb-6">
        <div className="brand-icon-badge"><FiMonitor /></div>
        <h1 className="text-xl font-semibold text-white">Remote Device Control</h1>
      </div>

      {/* Device Selection and Session Controls */}
      <div className="card-chrome mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <select
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            className="input-field min-w-[220px]"
          >
            <option value="">— Select device —</option>
            {Object.entries(devices)
              // Sort by name first (fallback to MAC) so the picker is browseable.
              .sort(([idA, a], [idB, b]) =>
                (a.name || idA).localeCompare(b.name || idB)
              )
              .map(([id, device]) => {
                const status = getDeviceStatus(device);
                const label = device.name?.trim()
                  ? `${device.name}  ·  ${id}`
                  : id;
                return (
                  <option key={id} value={id}>
                    {label} ({status.text})
                  </option>
                );
              })}
          </select>

          {!sessionInfo ? (
            <button
              onClick={startRemoteSession}
              disabled={!deviceId}
              className="btn-primary"
            >
              <FiPlay className="inline-block mr-2" /> Start Session
            </button>
          ) : (
            <button
              onClick={stopRemoteSession}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
            >
              <FiStopCircle className="inline-block mr-2" /> Stop Session
            </button>
          )}

          {/* Stream Status */}
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${
              streamStatus === 'connected' ? 'bg-green-500 animate-pulse' :
              streamStatus === 'connecting' || streamStatus === 'waiting' ? 'bg-yellow-500 animate-pulse' :
              streamStatus === 'error' ? 'bg-red-500' : 'bg-gray-500'
            }`} />
            <span className="text-sm text-gray-300 capitalize">{streamStatus}</span>
            {fps > 0 && <span className="text-sm text-green-400">({fps} FPS)</span>}
          </div>
        </div>

        {/* Active device identity — big name + MAC, then a quiet row of
            secondary details. Tells the operator at a glance which device
            they're remotely controlling. */}
        {selectedDevice && (
          <div className="mt-4 pt-4 border-t border-white/[0.06]">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h3 className="text-base font-semibold text-white">
                {selectedDevice.name?.trim() || <span className="font-mono">{deviceId}</span>}
              </h3>
              {selectedDevice.name?.trim() && (
                <span className="text-xs text-gray-500 font-mono">{deviceId}</span>
              )}
              {deviceStatus && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                  <span className={`w-2 h-2 rounded-full ${deviceStatus.color}`} />
                  {deviceStatus.text}
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-gray-500">
              {selectedDevice.customer_name && (
                <span>Customer: <span className="text-gray-300">{selectedDevice.customer_name}</span></span>
              )}
              {selectedDevice.ip_address && (
                <span>IP: <span className="text-gray-300 font-mono">{selectedDevice.ip_address}</span></span>
              )}
              {selectedDevice.device_model && (
                <span>Model: <span className="text-gray-300">{selectedDevice.device_model}</span></span>
              )}
              {selectedDevice.group && (
                <span>Group: <span className="text-gray-300">{selectedDevice.group}</span></span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Content - Screen and Controls */}
      <div className="flex flex-col xl:flex-row gap-6">
        {/* Screen View */}
        <div className="flex-1">
          <div className="card-chrome">
            {/* Screen Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Device Screen</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setClickMode(!clickMode)}
                  className={`p-2 rounded-md transition-colors border ${
                    clickMode
                      ? 'brand-pill'
                      : 'bg-white/[0.04] border-white/[0.08] text-gray-300 hover:bg-white/[0.08]'
                  }`}
                  title="Toggle Click Mode"
                >
                  <FiMousePointer size={18} />
                </button>
                <button
                  onClick={refreshScreenshot}
                  className="p-2 rounded-md bg-white/[0.04] border border-white/[0.08] text-gray-300 hover:bg-white/[0.08] transition-colors"
                  title="Refresh Screenshot"
                >
                  <FiRefreshCw size={18} />
                </button>
              </div>
            </div>

            {/* Screen Display */}
            <div
              className={`relative bg-black rounded-lg overflow-hidden ${
                clickMode ? 'cursor-crosshair' : 'cursor-default'
              }`}
              style={{ aspectRatio: '16/9' }}
            >
              {isStreaming && currentFrameUrl ? (
                <img
                  ref={screenRef}
                  src={currentFrameUrl}
                  alt="Device Screen"
                  className="w-full h-full object-contain"
                  onClick={handleScreenClick}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <FiVideo size={48} className="mx-auto mb-2 opacity-50" />
                    <p>
                      {streamStatus === 'waiting'
                        ? 'Waiting for device to start streaming...'
                        : streamStatus === 'connected' && !currentFrameUrl
                        ? 'Receiving frames from device...'
                        : 'Start a session to view device screen'}
                    </p>
                  </div>
                </div>
              )}

              {/* Click mode indicator */}
              {clickMode && isStreaming && (
                <div className="absolute top-2 left-2 brand-pill text-xs px-2 py-1 rounded">
                  Click Mode ON — click to tap
                </div>
              )}
            </div>

            {/* Text Input */}
            <div className="mt-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendTextInput()}
                  placeholder="Type text to send to device..."
                  className="input-field flex-1"
                />
                <button
                  onClick={sendTextInput}
                  disabled={!textInput.trim()}
                  className="btn-primary"
                >
                  <FiSend size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Remote Control */}
        <div className="w-full xl:w-80">
          <div className="card-chrome rounded-3xl p-5 sm:p-6">
            {/* Power Button */}
            <div className="flex justify-center mb-6">
              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('reboot')}
                  className="bg-red-500/20 text-red-300 p-4 rounded-full hover:bg-red-500/30 transition-colors"
                >
                  <FiPower size={24} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Reboot
                </span>
              </div>
            </div>

            {/* D-Pad */}
            <div className="relative flex justify-center items-center mb-6">
              <div className="relative w-36 h-36 rounded-full bg-surface-inset border border-white/[0.06] flex justify-center items-center">
                <div className="absolute w-32 h-32 rounded-full bg-gray-600 border-2 border-gray-500" />

                {/* Up */}
                <div className="absolute top-1 left-1/2 transform -translate-x-1/2 group">
                  <button
                    onClick={() => sendRemoteCommand('press_dpad_up')}
                    className="text-white p-3 rounded-full hover:bg-gray-500 transition-all transform hover:scale-105"
                  >
                    <FiArrowUp size={24} />
                  </button>
                </div>

                {/* Down */}
                <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 group">
                  <button
                    onClick={() => sendRemoteCommand('press_dpad_down')}
                    className="text-white p-3 rounded-full hover:bg-gray-500 transition-all transform hover:scale-105"
                  >
                    <FiArrowDown size={24} />
                  </button>
                </div>

                {/* Left */}
                <div className="absolute left-1 top-1/2 transform -translate-y-1/2 group">
                  <button
                    onClick={() => sendRemoteCommand('press_dpad_left')}
                    className="text-white p-3 rounded-full hover:bg-gray-500 transition-all transform hover:scale-105"
                  >
                    <FiArrowLeft size={24} />
                  </button>
                </div>

                {/* Right */}
                <div className="absolute right-1 top-1/2 transform -translate-y-1/2 group">
                  <button
                    onClick={() => sendRemoteCommand('press_dpad_right')}
                    className="text-white p-3 rounded-full hover:bg-gray-500 transition-all transform hover:scale-105"
                  >
                    <FiArrowRight size={24} />
                  </button>
                </div>

                {/* Center/OK */}
                <button
                  onClick={() => sendRemoteCommand('press_dpad_center')}
                  className="bg-brand text-white w-14 h-14 rounded-full hover:bg-brand-400 transition-colors z-10 font-bold text-sm"
                >
                  OK
                </button>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-center gap-4 mb-6">
              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('press_back')}
                  className="bg-brand/15 border border-brand/40 text-brand-400 p-4 rounded-full hover:bg-brand/25 transition-colors"
                >
                  <FiArrowLeft size={20} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Back
                </span>
              </div>

              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('press_home')}
                  className="bg-brand/15 border border-brand/40 text-brand-400 p-4 rounded-full hover:bg-brand/25 transition-colors"
                >
                  <FiHome size={20} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Home
                </span>
              </div>

              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('press_menu')}
                  className="bg-brand/15 border border-brand/40 text-brand-400 p-4 rounded-full hover:bg-brand/25 transition-colors"
                >
                  <FiSquare size={20} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Menu
                </span>
              </div>

              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('press_enter')}
                  className="bg-green-500/20 text-green-300 p-4 rounded-full hover:bg-green-500/30 transition-colors"
                >
                  <FiCornerDownLeft size={20} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Enter
                </span>
              </div>
            </div>

            {/* Volume Controls */}
            <div className="flex justify-center gap-3 mb-4">
              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('volume_down')}
                  className="bg-white/[0.05] border border-white/[0.08] text-gray-200 p-3 rounded-full hover:bg-white/[0.08] transition-colors"
                >
                  <FiMinus size={18} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Vol -
                </span>
              </div>

              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('mute')}
                  className="bg-white/[0.05] border border-white/[0.08] text-gray-200 p-3 rounded-full hover:bg-white/[0.08] transition-colors"
                >
                  <FiVolumeX size={18} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Mute
                </span>
              </div>

              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('volume_up')}
                  className="bg-white/[0.05] border border-white/[0.08] text-gray-200 p-3 rounded-full hover:bg-white/[0.08] transition-colors"
                >
                  <FiPlus size={18} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Vol +
                </span>
              </div>
            </div>

            {/* Additional Controls */}
            <div className="flex justify-center gap-3">
              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('media_play_pause')}
                  className="bg-white/[0.05] border border-white/[0.08] text-gray-200 p-3 rounded-full hover:bg-white/[0.08] transition-colors"
                >
                  <FiPlay size={18} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Play/Pause
                </span>
              </div>

              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('show_keyboard')}
                  className="bg-white/[0.05] border border-white/[0.08] text-gray-200 p-3 rounded-full hover:bg-white/[0.08] transition-colors"
                >
                  <FiType size={18} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Keyboard
                </span>
              </div>

              <div className="relative group">
                <button
                  onClick={() => sendRemoteCommand('rotate_screen')}
                  className="bg-white/[0.05] border border-white/[0.08] text-gray-200 p-3 rounded-full hover:bg-white/[0.08] transition-colors"
                >
                  <FiRotateCcw size={18} />
                </button>
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-surface-inset border border-white/[0.08] text-gray-200 text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Rotate
                </span>
              </div>
            </div>
          </div>

          {/* Session Info */}
          {sessionInfo && (
            <div className="mt-4 card-chrome">
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Session Info</h4>
              <p className="text-xs text-gray-400">ID: {sessionInfo.sessionId}</p>
              <p className="text-xs text-gray-400">
                Duration: {Math.floor((Date.now() - sessionInfo.startTime) / 1000)}s
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
